import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  hasProjectKnowledgeXlsxBlockedFile,
  PROJECT_KNOWLEDGE_XLSX_ERROR,
} from "../../../../core/services/fileUpload.service";
import {
  DARK_BORDER,
  formatFileSize,
  getFileAccentColor,
  glassPanelSx,
  HOME_GLASS_BORDER,
  isImageFile,
  mergeFiles,
  StepSectionTitle,
  THEME_ACCENT,
} from "./shared";
import { MODULE_LIST } from "../../../../constants/commons";
import { useAuth } from "../../../../app/context/AuthContext";

function ExistingFilesRow({
  files,
  iconColor,
  loading,
  deletingFileNames = [],
  downloadingFileNames = [],
  onDownloadExisting,
  onRemoveExisting,
}) {
  const deletingNames = useMemo(
    () => new Set(deletingFileNames.map(String)),
    [deletingFileNames],
  );

  const downloadingNames = useMemo(
    () => new Set(downloadingFileNames.map(String)),
    [downloadingFileNames],
  );

  // Keep the download control clear of the absolutely positioned delete button.
  const downloadSlotSx = {
    width: 18,
    height: 18,
    flexShrink: 0,
    mr: onRemoveExisting ? "13px" : 0,
  };

  if (loading) {
    return (
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1.25 }}>
        <CircularProgress size={14} sx={{ color: iconColor }} />
        <Typography sx={{ fontSize: 11.5, color: "rgba(255,255,255,0.45)" }}>
          Loading uploaded files...
        </Typography>
      </Stack>
    );
  }

  if (!files?.length) return null;

  return (
    <Box sx={{ mt: 1.25 }}>
      <Typography sx={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.45)", mb: 0.75 }}>
        Uploaded · {files.length} file{files.length === 1 ? "" : "s"}
      </Typography>
      <Box
        sx={{
          display: "flex",
          flexWrap: "nowrap",
          gap: 0.65,
          overflowX: "auto",
          overflowY: "hidden",
          pb: 0.35,
          WebkitOverflowScrolling: "touch",
          scrollbarWidth: "thin",
          scrollbarColor: "rgba(255,255,255,0.2) transparent",
          "&::-webkit-scrollbar": { height: 5 },
          "&::-webkit-scrollbar-thumb": {
            background: "rgba(255,255,255,0.2)",
            borderRadius: 999,
          },
        }}
      >
        {files.map((entry) => {
          const accent = getFileAccentColor({ name: entry.name }, iconColor);
          const isDeleting = deletingNames.has(entry.name);
          const isDownloading = downloadingNames.has(entry.name);
          const chipWidth = onDownloadExisting ? 104 : 82;

          return (
            <Box
              key={entry.id}
              title={entry.name}
              sx={{
                position: "relative",
                display: "flex",
                alignItems: "center",
                gap: 0.45,
                flex: `0 0 ${chipWidth}px`,
                width: chipWidth,
                minWidth: chipWidth,
                px: 0.5,
                py: 0.5,
                borderRadius: 1,
                background: "rgba(255,255,255,0.06)",
                border: "1px solid rgba(255,255,255,0.1)",
                opacity: isDeleting ? 0.55 : 1,
              }}
            >
              {entry.url ? (
                <Box
                  component="img"
                  src={entry.url}
                  alt={entry.name}
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: 0.5,
                    objectFit: "cover",
                    flexShrink: 0,
                  }}
                />
              ) : (
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: 0.5,
                    flexShrink: 0,
                    display: "grid",
                    placeItems: "center",
                    background: `${accent}18`,
                    border: `1px solid ${accent}33`,
                  }}
                >
                  <InsertDriveFileOutlinedIcon sx={{ fontSize: 13, color: accent }} />
                </Box>
              )}
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  pr: onRemoveExisting && !onDownloadExisting ? 1 : 0,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 9.5,
                    fontWeight: 600,
                    lineHeight: 1.25,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {entry.name}
                </Typography>
              </Box>
              {onDownloadExisting ? (
                isDownloading ? (
                  <Box sx={{ ...downloadSlotSx, display: "grid", placeItems: "center" }}>
                    <CircularProgress size={11} sx={{ color: accent }} />
                  </Box>
                ) : (
                  <Tooltip arrow placement="top" title={`Download ${entry.name}`}>
                    <IconButton
                      size="small"
                      aria-label={`Download ${entry.name}`}
                      onClick={() => onDownloadExisting(entry.name)}
                      sx={{
                        ...downloadSlotSx,
                        p: 0,
                        color: "rgba(255,255,255,0.5)",
                        "&:hover": { color: accent, background: "rgba(255,255,255,0.08)" },
                      }}
                    >
                      <DownloadOutlinedIcon sx={{ fontSize: 13 }} />
                    </IconButton>
                  </Tooltip>
                )
              ) : null}
              {onRemoveExisting ? (
                isDeleting ? (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 1,
                      right: 1,
                      width: 14,
                      height: 14,
                      display: "grid",
                      placeItems: "center",
                    }}
                  >
                    <CircularProgress size={10} sx={{ color: accent }} />
                  </Box>
                ) : (
                  <IconButton
                    size="small"
                    aria-label={`Delete ${entry.name}`}
                    onClick={() => onRemoveExisting(entry.name)}
                    sx={{
                      position: "absolute",
                      top: 1,
                      right: 1,
                      width: 14,
                      height: 14,
                      p: 0,
                      color: "rgba(255,255,255,0.45)",
                      "&:hover": { color: "#ff8a80", background: "rgba(255,255,255,0.06)" },
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 10 }} />
                  </IconButton>
                )
              ) : null}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

function AssetUploadRow({
  label,
  description,
  inputId,
  files,
  existingFiles = [],
  existingLoading = false,
  deletingFileNames = [],
  downloadingFileNames = [],
  onDownloadExisting,
  onRemoveExisting,
  multiple,
  accept,
  onSelect,
  icon: Icon,
  iconColor,
  blockXlsx = false,
  blockXlsxMessage = "",
}) {
  const inputRef = useRef(null);
  const previewUrlsRef = useRef(new Map());
  const [expanded, setExpanded] = useState(false);
  const [fileError, setFileError] = useState("");
  const selected = Array.isArray(files) ? files : files ? [files] : [];
  const hasFile = selected.length > 0;
  const hasExisting = existingFiles.length > 0 || existingLoading;

  const { userPermissions } = useAuth();

  const projKnowBankModule = Object.hasOwn(userPermissions, MODULE_LIST.PROJ_KNOW_BANK)
    ? userPermissions[MODULE_LIST.PROJ_KNOW_BANK]
    : false;
  const canEditProjKnowBank = projKnowBankModule.edit;

  const fileEntries = useMemo(
    () => selected.map((file) => ({ id: `${file.name}-${file.size}-${file.lastModified}`, file })),
    [selected],
  );

  const previews = useMemo(
    () =>
      fileEntries.map((entry) => {
        if (!isImageFile(entry.file)) return { ...entry, previewUrl: null };

        let previewUrl = previewUrlsRef.current.get(entry.id);
        if (!previewUrl) {
          previewUrl = URL.createObjectURL(entry.file);
          previewUrlsRef.current.set(entry.id, previewUrl);
        }
        return { ...entry, previewUrl };
      }),
    [fileEntries],
  );

  useEffect(
    () => () => {
      previewUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
      previewUrlsRef.current.clear();
    },
    [],
  );

  useEffect(() => {
    const activeIds = new Set(fileEntries.map((entry) => entry.id));
    previewUrlsRef.current.forEach((url, id) => {
      if (!activeIds.has(id)) {
        URL.revokeObjectURL(url);
        previewUrlsRef.current.delete(id);
      }
    });
  }, [fileEntries]);

  useEffect(() => {
    if (!hasFile) setExpanded(false);
  }, [hasFile]);

  const removeFile = useCallback(
    (id) => {
      const idx = fileEntries.findIndex((entry) => entry.id === id);
      if (idx < 0) return;
      const next = selected.filter((_, i) => i !== idx);
      onSelect(multiple ? next : next[0] ?? null);
    },
    [fileEntries, multiple, onSelect, selected],
  );

  const clearAll = useCallback(() => {
    setExpanded(false);
    setFileError("");
    onSelect(multiple ? [] : null);
  }, [multiple, onSelect]);

  const handleInputChange = (event) => {
    const list = Array.from(event.target.files || []).filter(Boolean);
    setFileError("");

    if (blockXlsx && hasProjectKnowledgeXlsxBlockedFile(list)) {
      setFileError(blockXlsxMessage || PROJECT_KNOWLEDGE_XLSX_ERROR);
      event.target.value = "";
      return;
    }

    if (multiple) onSelect(mergeFiles(selected, list));
    else onSelect(list[0] ?? null);
    event.target.value = "";
  };

  return (
    <Box sx={{ ...glassPanelSx, px: 1.75, py: 1.4, display: canEditProjKnowBank ? "block" : "none" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <Box
          sx={{
            width: 38,
            height: 38,
            borderRadius: "10px",
            flexShrink: 0,
            display: "grid",
            placeItems: "center",
            background: `${iconColor}18`,
            border: `1px solid ${iconColor}33`,
          }}
        >
          <Icon sx={{ fontSize: 19, color: iconColor }} />
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 0.15 }}>
            {label}
          </Typography>
          <Typography sx={{ fontSize: 11.5, color: "rgba(255,255,255,0.4)" }}>
            {hasFile
              ? `${selected.length} file${selected.length === 1 ? "" : "s"} selected`
              : existingLoading
                ? "Loading uploaded files..."
                : existingFiles.length
                  ? `${existingFiles.length} uploaded file${existingFiles.length === 1 ? "" : "s"}`
                  : description}
          </Typography>
        </Box>

        <Button
          component="label"
          htmlFor={inputId}
          size="small"
          sx={{
            flexShrink: 0,
            textTransform: "none",
            fontSize: 12,
            fontWeight: 600,
            borderRadius: "8px",
            px: 1.6,
            py: 0.65,
            color: "rgba(255,255,255,0.88)",
            background: "rgba(255,255,255,0.06)",
            border: `1px solid ${DARK_BORDER}`,
            "&:hover": { background: "rgba(255,255,255,0.1)" },
          }}
        >
          {hasFile || hasExisting ? "Add more" : multiple ? "Choose Files" : "Choose File"}
        </Button>

        <input
          ref={inputRef}
          id={inputId}
          type="file"
          hidden
          multiple={multiple}
          accept={accept}
          onChange={handleInputChange}
        />
      </Box>

      {hasExisting ? (
        <ExistingFilesRow
          files={existingFiles}
          iconColor={iconColor}
          loading={existingLoading}
          deletingFileNames={deletingFileNames}
          downloadingFileNames={downloadingFileNames}
          onDownloadExisting={onDownloadExisting}
          onRemoveExisting={onRemoveExisting}
        />
      ) : null}

      {fileError ? (
        <Typography
          sx={{
            mt: 1.1,
            fontSize: 12,
            color: "#ff8b8b",
            lineHeight: 1.4,
          }}
        >
          {fileError}
        </Typography>
      ) : null}

      {hasFile ? (
        <Box sx={{ mt: 1.25 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
            <Button
              size="small"
              onClick={() => setExpanded((open) => !open)}
              endIcon={expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
              sx={{
                minWidth: 0,
                px: 0.5,
                textTransform: "none",
                fontSize: 11.5,
                fontWeight: 600,
                color: "rgba(255,255,255,0.55)",
                "&:hover": { color: "rgba(255,255,255,0.85)", background: "rgba(255,255,255,0.04)" },
              }}
            >
              {selected.length} file{selected.length === 1 ? "" : "s"} ready · View all
            </Button>
            <Button
              size="small"
              onClick={clearAll}
              sx={{
                minWidth: 0,
                textTransform: "none",
                fontSize: 11,
                color: "rgba(255,255,255,0.45)",
                "&:hover": { color: "#ff8a80", background: "rgba(255,255,255,0.04)" },
              }}
            >
              Clear all
            </Button>
          </Stack>

          {expanded ? (
            <Box
              sx={{
                mt: 0.85,
                display: "flex",
                flexWrap: "nowrap",
                gap: 0.65,
                overflowX: "auto",
                overflowY: "hidden",
                pb: 0.35,
                WebkitOverflowScrolling: "touch",
                scrollbarWidth: "thin",
                scrollbarColor: "rgba(255,255,255,0.2) transparent",
                "&::-webkit-scrollbar": { height: 5 },
                "&::-webkit-scrollbar-thumb": {
                  background: "rgba(255,255,255,0.2)",
                  borderRadius: 999,
                },
              }}
            >
              {previews.map((entry) => {
                const accent = getFileAccentColor(entry.file, iconColor);
                return (
                  <Box
                    key={entry.id}
                    title={`${entry.file.name}${entry.file.size ? ` · ${formatFileSize(entry.file.size)}` : ""}`}
                    sx={{
                      position: "relative",
                      display: "flex",
                      alignItems: "center",
                      gap: 0.45,
                      flex: "0 0 82px",
                      width: 82,
                      minWidth: 82,
                      px: 0.5,
                      py: 0.5,
                      borderRadius: 1,
                      background: "rgba(255,255,255,0.06)",
                      border: "1px solid rgba(255,255,255,0.1)",
                    }}
                  >
                    {entry.previewUrl ? (
                      <Box
                        component="img"
                        src={entry.previewUrl}
                        alt={entry.file.name}
                        sx={{
                          width: 24,
                          height: 24,
                          borderRadius: 0.5,
                          objectFit: "cover",
                          flexShrink: 0,
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          width: 24,
                          height: 24,
                          borderRadius: 0.5,
                          flexShrink: 0,
                          display: "grid",
                          placeItems: "center",
                          background: `${accent}18`,
                          border: `1px solid ${accent}33`,
                        }}
                      >
                        <InsertDriveFileOutlinedIcon sx={{ fontSize: 13, color: accent }} />
                      </Box>
                    )}
                    <Box sx={{ flex: 1, minWidth: 0, pr: 1 }}>
                      <Typography
                        sx={{
                          fontSize: 9.5,
                          fontWeight: 600,
                          lineHeight: 1.25,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {entry.file.name}
                      </Typography>
                    </Box>
                    <IconButton
                      size="small"
                      aria-label={`Remove ${entry.file.name}`}
                      onClick={() => removeFile(entry.id)}
                      sx={{
                        position: "absolute",
                        top: 1,
                        right: 1,
                        width: 14,
                        height: 14,
                        p: 0,
                        color: "rgba(255,255,255,0.45)",
                        "&:hover": { color: "#ff8a80", background: "rgba(255,255,255,0.06)" },
                      }}
                    >
                      <CloseIcon sx={{ fontSize: 10 }} />
                    </IconButton>
                  </Box>
                );
              })}

              {multiple ? (
                <Box
                  role="button"
                  tabIndex={0}
                  onClick={() => inputRef.current?.click()}
                  onKeyDown={(event) => {
                    if (event.key !== "Enter" && event.key !== " ") return;
                    event.preventDefault();
                    inputRef.current?.click();
                  }}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 0.2,
                    flex: "0 0 52px",
                    width: 52,
                    minWidth: 52,
                    minHeight: 40,
                    px: 0.35,
                    borderRadius: 1,
                    border: "1px dashed rgba(255,255,255,0.2)",
                    cursor: "pointer",
                    color: "rgba(255,255,255,0.45)",
                    "&:hover": {
                      borderColor: `${iconColor}66`,
                      background: "rgba(255,255,255,0.03)",
                      color: "rgba(255,255,255,0.65)",
                    },
                  }}
                >
                  <AddIcon sx={{ fontSize: 13 }} />
                  <Typography sx={{ fontSize: 8.5, fontWeight: 500, textAlign: "center", lineHeight: 1.15 }}>
                    Add more
                  </Typography>
                </Box>
              ) : null}
            </Box>
          ) : null}
        </Box>
      ) : null}
    </Box>
  );
}

export default function ProjectAssetsStep({
  form,
  setForm,
  existingKnowledgeFiles = [],
  existingKnowledgeLoading = false,
  deletingKnowledgeFileNames = [],
  downloadingKnowledgeFileNames = [],
  onDownloadExistingFile,
  onRemoveExistingKnowledgeFile,
}) {
  return (
    <>
      <StepSectionTitle icon={FolderOutlinedIcon}>Knowledge Base</StepSectionTitle>
      <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,0.45)", mb: 1.5, lineHeight: 1.5 }}>
        Upload knowledge base files for this project.
      </Typography>
      <Stack spacing={1}>
        <AssetUploadRow
          label="Knowledge Bank"
          description="Upload knowledge base files"
          inputId="create-project-kb"
          files={form.knowledgeBank}
          existingFiles={existingKnowledgeFiles}
          existingLoading={existingKnowledgeLoading}
          deletingFileNames={deletingKnowledgeFileNames}
          downloadingFileNames={downloadingKnowledgeFileNames}
          onDownloadExisting={onDownloadExistingFile}
          onRemoveExisting={onRemoveExistingKnowledgeFile}
          multiple
          blockXlsx
          blockXlsxMessage={PROJECT_KNOWLEDGE_XLSX_ERROR}
          icon={TableChartOutlinedIcon}
          iconColor="#22C55E"
          onSelect={(files) =>
            setForm((p) => ({ ...p, knowledgeBank: files }))
          }
        />
      </Stack>

      <Box
        sx={{
          mt: 1.5,
          px: 1.5,
          py: 1.1,
          borderRadius: "10px",
          display: "flex",
          alignItems: "flex-start",
          gap: 1,
          background: "rgba(255,255,255,0.05)",
          border: HOME_GLASS_BORDER,
        }}
      >
        <InfoOutlinedIcon
          sx={{ fontSize: 16, color: THEME_ACCENT, mt: 0.1, flexShrink: 0 }}
        />
        <Typography sx={{ fontSize: 11.5, color: "rgba(255,255,255,0.55)", lineHeight: 1.5 }}>
          Upload documents, PDFs, and other reference files for this project.
          Excel (.xlsx / .xls) is not accepted.
          {existingKnowledgeFiles.length || existingKnowledgeLoading
            ? " Use the download icon to save an uploaded file, or the × to delete."
            : ""}
        </Typography>
      </Box>
    </>
  );
}
